import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { css } from '@emotion/css';

import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import {
  aspectoDeRecuerdo,
  explicarAspecto,
  textoRitmo,
  textoSello,
} from '#components/mobile/budget/recuerdosAspecto';
import { Botella } from '#components/mobile/budget/travesia/Botella';
import { e, SERIF } from '#components/mobile/budget/travesia/escenaCss';
import { Sello } from '#components/mobile/budget/travesia/Sello';
import { TACTIL } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';

import { nombreDe } from './bitacora';
import type { Cumplida, Totales } from './bitacora';

/** Lo que lleva una travesía en curso (del detector de llegadas). */
export type EnCurso = { saldo: IntegerAmount; meta: IntegerAmount };

function useCifras() {
  const format = useFormat();
  const locale = useLocale();
  return {
    importe: (v: IntegerAmount) =>
      format(v, v % 100 === 0 ? 'financial-no-decimals' : 'financial'),
    fecha: (dia: string, patron = 'd MMM yyyy') =>
      monthUtils.format(dia, patron, locale),
  };
}

function Cabecera({ derecha }: { derecha?: ReactNode }) {
  return (
    <div className="cab">
      <span>
        <Trans>Logbook</Trans>
      </span>
      {derecha && <span className="tnum">{derecha}</span>}
    </div>
  );
}

function Fila({
  etiqueta,
  children,
  testId,
}: {
  etiqueta: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="fila" data-testid={testId}>
      <span>{etiqueta}</span>
      <span className="tnum">{children}</span>
    </div>
  );
}

function duracion(
  t: ReturnType<typeof useTranslation>['t'],
  c: Cumplida,
): string {
  return c.meses >= 1
    ? t('{{count}} months at sea', { count: c.meses })
    : t('{{count}} days at sea', { count: Math.max(1, c.dias) });
}

/** Primera página sin travesías cumplidas: estado vacío digno. */
export function PaginaVacia({ hayNavegando }: { hayNavegando: boolean }) {
  return (
    <div className={clasePagina} data-testid="bitacora-vacia">
      <Cabecera />
      <h2>
        <Trans>Your logbook</Trans>
      </h2>
      <p className="parrafo">
        <Trans>
          No bottle has arrived yet. When a voyage reaches port, its letter and
          its stamp will be kept here.
        </Trans>
      </p>
      <p className="margen">
        {hayNavegando ? (
          <Trans>Turn the page to see the voyages still at sea.</Trans>
        ) : (
          <Trans>
            To set sail, give a category a goal with a date and write it a
            letter.
          </Trans>
        )}
      </p>
    </div>
  );
}

/** «Hoja de totales»: lo hecho, con las rarezas anotadas al margen. */
export function PaginaTotales({ totales }: { totales: Totales }) {
  const { t } = useTranslation();
  const { importe, fecha } = useCifras();
  const { primera, masLarga, numero } = totales;
  return (
    <div className={clasePagina} data-testid="hoja-totales">
      <Cabecera />
      <h2>
        <Trans>Totals sheet</Trans>
      </h2>
      <div className="total">
        <PrivacyFilter>
          <span className="tnum cifra" data-testid="totales-importe">
            {importe(totales.total)}
          </span>
        </PrivacyFilter>
        <span className="rotulo">
          <Trans>saved on voyages that arrived</Trans>
        </span>
      </div>
      <Fila etiqueta={t('Voyages that arrived')} testId="totales-numero">
        {String(numero)}
      </Fila>
      <div className="margen" data-testid="totales-margen">
        {primera && (
          <p>
            {t('The first: {{name}}, arrived on {{date}}.', {
              name: nombreDe(primera.recuerdo),
              date: fecha(primera.llegada),
            })}
          </p>
        )}
        {masLarga && numero > 1 && masLarga !== primera && (
          <p>
            {t('The longest: {{name}}, {{duration}}.', {
              name: nombreDe(masLarga.recuerdo),
              duration: duracion(t, masLarga),
            })}
          </p>
        )}
        <p>
          <Trans>Tap a bottle on the shelf to open its page.</Trans>
        </p>
      </div>
    </div>
  );
}

/** Una travesía cumplida: carta, ficha, lectura de la botella y sello. */
export function PaginaTravesia({
  cumplida: c,
  hoy,
  porAbrir,
  onAbrir,
}: {
  cumplida: Cumplida;
  hoy: string;
  /** Este dispositivo aún no la ha abierto: la carta espera. */
  porAbrir: boolean;
  onAbrir: () => void;
}) {
  const { t } = useTranslation();
  const { importe, fecha } = useCifras();
  const r = c.recuerdo;
  const llegada = r.llegada;
  const aspecto = aspectoDeRecuerdo(r, hoy);
  const lectura = explicarAspecto(
    t,
    aspecto,
    importe(c.importe),
    c.aportaciones ?? 0,
  );
  const cerradaHasta =
    r.base.abrirDesde && hoy < r.base.abrirDesde ? r.base.abrirDesde : null;
  const ritmo = llegada
    ? [
        textoRitmo(t, llegada.diasRespectoPlazo),
        llegada.ajustada ? t('goal adjusted') : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;
  const notas = [
    lectura.vidrio,
    lectura.altura,
    lectura.patina,
    lectura.lacre,
    c.aportaciones ? lectura.marcas : null,
    lectura.sello,
  ].filter((n): n is string => !!n);

  return (
    <div
      className={clasePagina}
      data-testid="pagina-travesia"
      data-rid={r.base.rid}
    >
      <Cabecera derecha={t('Voyage no. {{number}}', { number: c.numero })} />
      <h2>{nombreDe(r)}</h2>

      <section className="carta" data-testid="pagina-carta">
        <div className="sub">
          <span>
            <Trans>Letter from the start</Trans>
          </span>
          <span className="tnum">{fecha(r.base.zarpoEn)}</span>
        </div>
        {cerradaHasta ? (
          <p className="parrafo">
            {t('It has arrived. It opens on {{date}}.', {
              date: fecha(cerradaHasta, 'd MMMM yyyy'),
            })}
          </p>
        ) : porAbrir ? (
          <div className="por-abrir">
            <p className="parrafo">
              <Trans>It has arrived.</Trans>
            </p>
            <button
              type="button"
              className="pri"
              onClick={onAbrir}
              data-testid="pagina-abrir"
            >
              <Trans>Open the bottle</Trans>
            </button>
          </div>
        ) : (
          <>
            <p className="txt">
              {r.base.carta || t('This voyage set sail without a letter.')}
            </p>
            {r.base.autor && <p className="firma">— {r.base.autor}</p>}
            {r.firmas.map(f => (
              <p key={f.disp} className="firma-otra">
                {f.texto}
                {f.quien && <span> — {f.quien}</span>}
              </p>
            ))}
            {r.respuestas.map(f => (
              <p
                key={`r${f.disp}`}
                className="respuesta"
                data-testid="pagina-respuesta"
              >
                {f.texto}
                {f.quien && <span> — {f.quien}</span>}
              </p>
            ))}
          </>
        )}
      </section>

      <section className="ficha" data-testid="pagina-ficha">
        <Fila etiqueta={t('Amount')}>
          <PrivacyFilter>{importe(c.importe)}</PrivacyFilter>
        </Fila>
        <Fila etiqueta={t('Departed')}>{fecha(r.base.zarpoEn)}</Fila>
        <Fila etiqueta={t('Arrival')}>{fecha(c.llegada)}</Fila>
        <Fila etiqueta={t('Voyage duration')}>{duracion(t, c)}</Fila>
        {c.aportaciones != null && (
          <Fila etiqueta={t('Contributions')}>{String(c.aportaciones)}</Fila>
        )}
        {c.aportacionMedia != null && (
          <Fila etiqueta={t('Average contribution')}>
            <PrivacyFilter>{importe(c.aportacionMedia)}</PrivacyFilter>
          </Fila>
        )}
        {ritmo && <Fila etiqueta={t('Voyage pace')}>{ritmo}</Fila>}
      </section>

      <div className="pie-pagina">
        <div className="margen" data-testid="pagina-lectura">
          {notas.map(n => (
            <p key={n}>{n}.</p>
          ))}
        </div>
        <Sello
          aspecto={aspecto}
          texto={textoSello(r.base.nombre, c.llegada)}
          tamano={108}
          className="sello"
        />
      </div>
    </div>
  );
}

/** «Navegando»: lo único pendiente que se ve, con lo que llevas. */
export function PaginaNavegando({
  recuerdos,
  hoy,
  enCurso,
  onIrCategoria,
}: {
  recuerdos: readonly Recuerdo[];
  hoy: string;
  enCurso: ReadonlyMap<string, EnCurso>;
  onIrCategoria: (categoriaId: string) => void;
}) {
  const { t } = useTranslation();
  const { importe, fecha } = useCifras();
  return (
    <div className={clasePagina} data-testid="pagina-navegando">
      <Cabecera />
      <h2>
        <Trans>At sea</Trans>
      </h2>
      <ul className="navegando">
        {recuerdos.map(r => {
          const vivo = enCurso.get(r.base.rid);
          return (
            <li key={r.base.rid}>
              <button
                type="button"
                onClick={() => onIrCategoria(r.base.categoriaId)}
                data-testid="navegando-travesia"
              >
                <span className="mini-nav" aria-hidden>
                  <Botella aspecto={aspectoDeRecuerdo(r, hoy)} ancho={20} />
                </span>
                <span className="datos">
                  <span className="nombre">{nombreDe(r)}</span>
                  <span className="linea tnum">
                    {vivo ? (
                      <PrivacyFilter>
                        {t('So far {{saved}} of {{goal}}', {
                          saved: importe(Math.max(0, vivo.saldo)),
                          goal: importe(vivo.meta),
                        })}
                      </PrivacyFilter>
                    ) : (
                      t('Set sail on {{date}}', { date: fecha(r.base.zarpoEn) })
                    )}
                  </span>
                  <span className="linea tnum">
                    {t('Due {{date}}', { date: fecha(r.base.ciclo) })}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const clasePagina = css({
  '& .cab, & .sub': {
    fontSize: 13.5,
    letterSpacing: '.12em',
    fontVariant: 'small-caps',
    textTransform: 'lowercase',
    color: e.tinta2,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
  },
  '& .sub': { letterSpacing: '.09em', fontSize: 13 },
  '& h2': {
    fontWeight: 400,
    fontSize: 30,
    lineHeight: 1.15,
    margin: '6px 0 12px',
    overflowWrap: 'anywhere',
    fontFamily: SERIF,
  },
  '& .parrafo': {
    fontSize: 17,
    lineHeight: 1.5,
    margin: '8px 0 0',
    textWrap: 'pretty',
  },
  '& .margen': {
    marginTop: 14,
    paddingLeft: 12,
    borderLeft: '1px solid rgba(58,46,36,.22)',
    fontStyle: 'italic',
    fontSize: 14,
    lineHeight: 1.5,
    color: e.tinta2,
    '& p': { margin: '0 0 4px' },
  },
  '& .total': {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 0 10px',
    borderBottom: '1px solid rgba(58,46,36,.16)',
  },
  '& .cifra': { fontSize: 36, lineHeight: 1.15, letterSpacing: '.01em' },
  '& .rotulo': {
    fontVariant: 'small-caps',
    textTransform: 'lowercase',
    letterSpacing: '.06em',
    color: e.tinta2,
    fontSize: 16,
  },
  '& .fila': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    padding: '7px 0',
    borderBottom: '1px solid rgba(58,46,36,.16)',
    fontSize: 15.5,
    '& > span:first-of-type': {
      fontVariant: 'small-caps',
      textTransform: 'lowercase',
      letterSpacing: '.06em',
      color: e.tinta2,
      fontSize: 15.5,
      flex: 'none',
    },
    '& > span:last-of-type': { textAlign: 'right', minWidth: 0 },
  },
  '& .carta': {
    padding: '10px 0 14px',
    marginBottom: 6,
    borderTop: '1px solid rgba(58,46,36,.2)',
    borderBottom: '1px solid rgba(58,46,36,.2)',
  },
  '& .txt': {
    fontStyle: 'italic',
    fontSize: 17.5,
    lineHeight: 1.48,
    margin: '10px 0 0',
    textWrap: 'pretty',
    whiteSpace: 'pre-line',
  },
  '& .firma': {
    textAlign: 'right',
    fontStyle: 'italic',
    fontSize: 16,
    margin: '8px 0 0',
  },
  '& .firma-otra, & .respuesta': {
    fontStyle: 'italic',
    fontSize: 15.5,
    lineHeight: 1.45,
    margin: '8px 0 0',
  },
  '& .respuesta': {
    paddingTop: 8,
    borderTop: '1px dashed rgba(58,46,36,.25)',
    color: e.tinta2,
  },
  '& .por-abrir': { display: 'flex', flexDirection: 'column', gap: 10 },
  '& .pri': {
    alignSelf: 'flex-start',
    minHeight: TACTIL,
    padding: '0 18px',
    borderRadius: 10,
    border: 0,
    background: e.laton2,
    color: '#1f1810',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
  },
  '& .pie-pagina': {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
    marginTop: 4,
    '& .margen': { flex: 1, minWidth: 0 },
    '& .sello': { flexShrink: 0, opacity: 0.95, marginBottom: -6 },
  },
  '& .navegando': {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': { borderBottom: '1px solid rgba(58,46,36,.16)' },
    '& button': {
      width: '100%',
      minHeight: TACTIL + 12,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '10px 0',
      border: 0,
      background: 'none',
      color: 'inherit',
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
    },
  },
  '& .mini-nav': {
    width: 28,
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
  },
  '& .datos': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: 2,
  },
  '& .nombre': { fontSize: 18, overflowWrap: 'anywhere' },
  '& .linea': { fontSize: 14, color: e.tinta2 },
});
